import React, { memo, useCallback, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { TaskRow } from './task-row';
import { PulseSequence } from '@shared/schema';
import { useDebugRender } from '@/hooks/use-debug-render';
import { logger } from '@/services/debug-logger';
import { consolidateLinkedSharePulses } from '@/lib/linked-share';
import { useManualExpandScroll } from '@/components/project-tree/hooks/useManualExpandScroll';

interface VirtualizedPulseListProps {
  pulses: any[];
  projectId: string;
  pulseSummaries?: Record<number, any>;
  pulseMetadata?: Record<number, any>;
  isBatchLoading?: boolean;
  pulseSequences?: Record<number, PulseSequence[]>;
  expandedItems: Record<string, boolean>;
  toggleExpanded: (id: string) => void;
  setAddingTaskForPulse: (id: number | null) => void;
  addingTaskForPulse?: number | null;
  renderTaskTree: (tasks: any[], projectId: string, depth: number) => React.ReactNode;
  selectedSequences?: string[];
  height?: number;
  itemSize?: number;
  isProjectDeactivated?: boolean;
  onTrackTime?: (projectId: string, pulseId: number, event: React.MouseEvent) => void;
  shouldShowTimeTracking?: boolean;
  onOpenSettings?: (pulse: any) => void;
  onShareClick?: (pulse: any) => void;
  pulseReactions?: Record<string, any[]>;
  manualExpandIntent?: number;
  onManualExpandComplete?: (intent: number) => void;
}

const PulseRowComponent = memo(({ 
  index, 
  style, 
  data 
}: { 
  index: number; 
  style: React.CSSProperties; 
  data: any 
}) => {
  const {
    pulses,
    projectId,
    pulseSummaries,
    pulseMetadata,
    isBatchLoading,
    pulseSequences,
    expandedItems,
    toggleExpanded,
    setAddingTaskForPulse,
    addingTaskForPulse,
    renderTaskTree,
    selectedSequences,
    isProjectDeactivated,
    onTrackTime,
    shouldShowTimeTracking,
    onOpenSettings,
    onShareClick,
    pulseReactions
  } = data;
  
  const pulse = pulses[index];
  const isExpanded = expandedItems[`pulse-${pulse.id}`] || false;
  
  const sequences = pulseSequences?.[pulse.id] || [];
  
  if (selectedSequences && selectedSequences.length > 0 && sequences) {
    const hasSelectedSequence = sequences.some((ps: any) => 
      selectedSequences.includes(ps.sequenceNumber)
    );
    if (!hasSelectedSequence) {
      return null;
    }
  }
  
  return (
    <div style={style} id={`pulse-row-${pulse.id}`}>
      <TaskRow
        task={pulse}
        projectId={projectId}
        depth={0}
        isExpanded={isExpanded}
        onToggleExpanded={() => toggleExpanded(`pulse-${pulse.id}`)}
        isPulse={true}
        pulseSummary={pulseSummaries?.[pulse.id]}
        pulseMetadata={pulseMetadata?.[pulse.id]}
        isBatchContext={isBatchLoading}
        onAddTaskClick={isProjectDeactivated ? undefined : () => {
          if (!isExpanded) {
            toggleExpanded(`pulse-${pulse.id}`);
          }
          setAddingTaskForPulse(pulse.id);
        }}
        onTrackTime={onTrackTime ? (e) => onTrackTime(projectId, pulse.id, e) : undefined}
        shouldShowTimeTracking={shouldShowTimeTracking}
        onOpenSettings={onOpenSettings}
        onShareClick={onShareClick}
        pulseReactions={pulseReactions?.[String(pulse.id)]}
      />
    </div>
  );
});

PulseRowComponent.displayName = 'PulseRowComponent';

export function VirtualizedPulseList({
  pulses,
  projectId,
  pulseSummaries,
  pulseMetadata,
  isBatchLoading,
  pulseSequences,
  expandedItems,
  toggleExpanded,
  setAddingTaskForPulse,
  addingTaskForPulse,
  renderTaskTree,
  selectedSequences,
  height = 600,
  itemSize = 48,
  isProjectDeactivated = false,
  onTrackTime,
  shouldShowTimeTracking = false,
  onOpenSettings,
  onShareClick,
  pulseReactions,
  manualExpandIntent,
  onManualExpandComplete
}: VirtualizedPulseListProps) {
  
  useDebugRender('VirtualizedPulseList', { pulseCount: pulses?.length, projectId });
  
  const filteredPulses = useMemo(() => {
    logger.logTableRefresh('PulseList', 'pulses data changed', pulses?.length);
    if (!selectedSequences || selectedSequences.length === 0) {
      return consolidateLinkedSharePulses(pulses);
    }
    
    return consolidateLinkedSharePulses(pulses.filter(pulse => {
      const sequences = pulseSequences?.[pulse.id] || [];
      return sequences.some((ps: any) => 
        selectedSequences.includes(ps.sequenceNumber)
      );
    }));
  }, [pulses, pulseSequences, selectedSequences]);

  const listRef = useRef<List>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const positionLastItem = useCallback((index: number) => {
    listRef.current?.scrollToItem(index, 'end');
  }, []);
  const getListViewport = useCallback(() => viewportRef.current, []);
  const displayedPulseIds = useMemo(() => filteredPulses.map(pulse => pulse.id), [filteredPulses]);
  const startAtNewest = useManualExpandScroll({
    intent: manualExpandIntent,
    displayedPulseIds,
    onComplete: onManualExpandComplete,
    positionLastItem,
    getListViewport,
  });
  
  if (!filteredPulses || filteredPulses.length === 0) {
    return null;
  }
  
  // Review cards have variable height; fixed-size virtualization would overlap rows.
  if (filteredPulses.length < 50 || filteredPulses.some(pulse => pulse.category === 'client_files')) {
    return (
      <>
        {filteredPulses.map((pulse) => (
          <div key={`pulse-${pulse.id}`} id={`pulse-row-${pulse.id}`}>
            <TaskRow
              task={pulse}
              projectId={projectId}
              depth={0}
              isExpanded={expandedItems[`pulse-${pulse.id}`] || false}
              onToggleExpanded={() => toggleExpanded(`pulse-${pulse.id}`)}
              isPulse={true}
              pulseSummary={pulseSummaries?.[pulse.id]}
              pulseMetadata={pulseMetadata?.[pulse.id]}
              isBatchContext={isBatchLoading}
              onAddTaskClick={isProjectDeactivated ? undefined : () => {
                if (!expandedItems[`pulse-${pulse.id}`]) {
                  toggleExpanded(`pulse-${pulse.id}`);
                }
                setAddingTaskForPulse(pulse.id);
              }}
              onTrackTime={onTrackTime ? (e) => onTrackTime(projectId, pulse.id, e) : undefined}
              shouldShowTimeTracking={shouldShowTimeTracking}
              onOpenSettings={onOpenSettings}
              onShareClick={onShareClick}
              pulseReactions={pulseReactions?.[String(pulse.id)]}
            />
          </div>
        ))}
      </>
    );
  }
  
  const itemData = {
    pulses: filteredPulses,
    projectId,
    pulseSummaries,
    pulseMetadata,
    isBatchLoading,
    pulseSequences,
    expandedItems,
    toggleExpanded,
    setAddingTaskForPulse,
    addingTaskForPulse,
    renderTaskTree,
    selectedSequences,
    isProjectDeactivated,
    onTrackTime,
    shouldShowTimeTracking,
    onOpenSettings,
    onShareClick,
    pulseReactions
  };
  
  return (
    <List
      ref={listRef}
      outerRef={viewportRef}
      height={height}
      itemCount={filteredPulses.length}
      itemSize={itemSize}
      width="100%"
      itemData={itemData}
      overscanCount={5}
      initialScrollOffset={startAtNewest ? Math.max(0, filteredPulses.length * itemSize - height) : 0}
    >
      {PulseRowComponent}
    </List>
  );
}
